import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { BookOpen, Menu, X, User, Calendar, LogOut, Zap, Trophy, ClipboardList, History, LayoutDashboard, FileText, Archive, type LucideIcon } from 'lucide-react';
import { getStoredSession, clearStoredSession } from '../hooks/useAuth';
import { isDrillEnabled } from '../services/drillService';
import { isPracticeTestEnabled } from '../config/features';
import { InstallAppButton } from './InstallAppButton';
import { getStudentSelfProfile, getStudentBadges, type StudentSelfProfile } from '../services/conceptPlus1Service';
import { supabase } from '../services/supabaseClient';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
}

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profile, setProfile] = useState<StudentSelfProfile | null>(null);
  const [badgeCount, setBadgeCount] = useState(0);
  const [schoolLogoUrl, setSchoolLogoUrl] = useState<string | null>(null);
  const session = getStoredSession();

  useEffect(() => {
    if (!session?.student_id) return;
    getStudentSelfProfile(session.student_id).then(p => { if (p) setProfile(p); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.student_id]);

  useEffect(() => {
    if (!session?.student_id) return;
    getStudentBadges(session.student_id).then(badges => {
      setBadgeCount(badges.filter(b => b.is_earned).length);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.student_id]);

  useEffect(() => {
    if (!session?.school_id) return;
    supabase.rpc('get_school_logo', { p_school_id: session.school_id })
      .then(({ data }) => { if (data) setSchoolLogoUrl(data); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.school_id]);

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');

  const navItems: NavItem[] = [
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { to: '/concepts', label: 'Concepts', icon: BookOpen },
    { to: '/assignments', label: 'Assignments', icon: ClipboardList },
    ...(isPracticeTestEnabled(session?.grade_level ?? 0, session?.board)
      ? [
          { to: '/practice-test', label: 'Exam Simulator', icon: FileText },
          { to: '/practice-test/archive', label: 'Past Papers', icon: Archive },
          { to: '/practice-history', label: 'Practice History', icon: History },
        ]
      : []),
    ...(isDrillEnabled(session?.grade_level ?? 0, session?.board) ? [{ to: '/drill', label: 'Drill', icon: Zap }] : []),
  ];

  const handleLogout = () => {
    clearStoredSession();
    navigate('/welcome');
  };

  // Calculate days remaining
  const getDaysRemaining = () => {
    if (!session?.expires_at) return 0;
    const expiryDate = new Date(session.expires_at);
    const now = new Date();
    const diffTime = expiryDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  return (
    <nav className="fixed top-4 left-0 right-0 z-50 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="glass-panel rounded-2xl px-4 sm:px-6 h-16 flex items-center justify-between shadow-lg shadow-black/5 transition-all duration-300 hover:shadow-black/10">

          {/* LEFT - Logo */}
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <Link to="/dashboard" className="flex items-center gap-2 cursor-pointer group">
              {schoolLogoUrl ? (
                <img src={schoolLogoUrl} alt="School Logo" className="max-h-14 w-auto max-w-[300px] object-contain group-hover:opacity-90 transition-opacity" />
              ) : (
                <>
                  <img src="/logo.png" alt="Branon Logo" className="h-10 w-10 group-hover:scale-105 transition-transform duration-300" />
                  <img src="/wordmark.png" alt="Branon" className="h-7 hidden sm:block group-hover:opacity-80 transition-opacity" />
                  <span className="text-coral font-bold text-lg">+</span>
                </>
              )}
            </Link>
          </div>

          {/* CENTER - Desktop Navigation -- icon-only for inactive tabs, icon+label
              for the active tab, so all items fit at real laptop widths without
              hiding any of them behind an overflow scroll. */}
          {session && (
            <div className="hidden md:flex items-center flex-1 min-w-0 justify-center mx-2">
              <div className="flex items-center gap-1">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.to);
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      title={item.label}
                      className={`flex items-center gap-1.5 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap flex-shrink-0 ${
                        active ? 'px-3 py-1.5 bg-coral text-white shadow-sm' : 'p-2 text-gray-500 hover:text-ink hover:bg-gray-50'
                      }`}
                    >
                      <Icon className="h-4 w-4 flex-shrink-0" />
                      {active && item.label}
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* RIGHT - User Info / Actions */}
          <div className="flex items-center gap-2 sm:gap-4">
            {session ? (
              <>
                {/* Desktop User Info */}
                <div className="hidden md:flex items-center gap-4">
                  <Link to="/badges" className="relative rounded-xl bg-marigold p-2 hover:opacity-90 transition-opacity text-ink" aria-label="My badges">
                    <Trophy className="h-5 w-5" />
                    {badgeCount > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 bg-coral text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 leading-none border-2 border-white">
                        {badgeCount > 99 ? '99+' : badgeCount}
                      </span>
                    )}
                  </Link>
                  <InstallAppButton />
                  <div className="text-right">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-500" />
                      <span className="text-sm font-semibold text-ink">
                        {profile?.full_name || session.student_code}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      {profile?.roll_number && <span>Roll {profile.roll_number}</span>}
                      {profile?.roll_number && <span>·</span>}
                      <span>Gr.{profile?.grade_level ?? session.grade_level}{profile?.section_name ? ` ${profile.section_name}` : ''}</span>
                      <span>·</span>
                      <Calendar className="h-3 w-3" />
                      <span>{getDaysRemaining()} days left</span>
                    </div>
                  </div>

                  {/* Logout Button */}
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-50 text-ink border-2 border-ink rounded-xl transition-all text-sm font-bold shadow-sticker hover:translate-y-0.5 hover:shadow-none"
                  >
                    <LogOut className="h-4 w-4" />
                    <span className="hidden lg:inline">Logout</span>
                  </button>
                </div>

                {/* Mobile Menu Toggle */}
                <div className="md:hidden">
                  <button
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
                  >
                    {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                  </button>
                </div>
              </>
            ) : (
              <Link
                to="/login"
                className="px-5 py-2 bg-coral text-white border-2 border-ink rounded-xl transition-all text-sm font-bold shadow-sticker hover:translate-y-0.5 hover:shadow-none"
              >
                Login
              </Link>
            )}
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {session && mobileMenuOpen && (
          <div className="md:hidden mt-2 glass-panel rounded-2xl overflow-hidden shadow-lg animate-fade-in-up">
            <div className="p-4 space-y-1.5">
              {navItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.to);
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`flex items-center gap-2.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      active ? 'bg-coral text-white' : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                  </Link>
                );
              })}
              <Link
                to="/badges"
                className="flex items-center gap-2.5 px-4 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Trophy className="w-4 h-4 text-marigold" />
                Badges{badgeCount > 0 ? ` (${badgeCount})` : ''}
              </Link>

              <div className="pt-3 border-t border-gray-200">
                <div className="px-4 py-2 text-sm text-gray-600">
                  <div className="font-bold text-ink">{profile?.full_name || session.student_code}</div>
                  {profile?.roll_number && <div className="text-xs text-gray-500">Roll {profile.roll_number}</div>}
                  <div className="text-xs text-gray-500">
                    Grade {profile?.grade_level ?? session.grade_level}{profile?.section_name ? ` · ${profile.section_name}` : ''}
                  </div>
                  <div className="text-xs text-gray-500">{getDaysRemaining()} days remaining</div>
                </div>
                <button
                  onClick={() => {
                    handleLogout();
                    setMobileMenuOpen(false);
                  }}
                  className="w-full mt-2 px-4 py-2 bg-white border-2 border-ink text-ink rounded-lg transition-colors text-sm font-bold flex items-center justify-center gap-2"
                >
                  <LogOut className="h-4 w-4" />
                  Logout
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
